"use client";
import { Spinner } from "@/components/spinner";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { normalizeTicker } from "@/lib/utils";
import { OptionsContext } from "@/provider/options-provider";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { useContext, useState } from "react";

export function CombinePdfButton(props: {
  className?: React.ComponentProps<"button">["className"];
}) {
  const { groupSummaryItems, searchOptions } = useContext(OptionsContext);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const disabled = groupSummaryItems.length === 0 || loading;

  const handleClick = async () => {
    setLoading(true);
    try {
      const ids = groupSummaryItems.map((item) => `${item.type}${item.id}`);
      const requestBody: { ids: string[]; ticker?: string } = { ids };

      if (searchOptions.ticker && searchOptions.ticker.trim()) {
        requestBody.ticker = normalizeTicker(searchOptions.ticker);
      }

      const response = await fetch("/api/ai/combine", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestBody),
      });

      if (response.ok) {
        const result = await response.json();
        toast({
          title: "Request received",
          description: `${result?.message ? `${result.message}. ` : ""}`,
        });
      } else {
        toast({
          title: "Error",
          description: `Failed to submit the combine request. Please report to engineering. (${JSON.stringify(await response.json())})`,
          variant: "destructive",
        });
      }
    } catch {
      toast({
        title: "Error",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      onClick={handleClick}
      className={props.className}
      type="button"
      tabIndex={-1}
      variant={"outline"}
      size={"icon"}
      disabled={disabled}
      title="Request combined PDF"
    >
      {loading ? (
        <Spinner size={16} />
      ) : (
        <PictureAsPdfIcon className="h-4 w-4" />
      )}
    </Button>
  );
}
